import { useEffect } from 'react';
import { useParams, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supportedLanguages, type SupportedLanguage } from '@/i18n';
import PageTransition from '@/components/PageTransition';

const LanguageWrapper = () => {
  const { lang } = useParams<{ lang: string }>();
  const { i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    if (lang && supportedLanguages.includes(lang as SupportedLanguage)) {
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang);
      }
      // Update <html lang> attribute for SEO
      document.documentElement.lang = lang;
    }
  }, [lang, i18n]);

  // If invalid language, redirect to default
  if (!lang || !supportedLanguages.includes(lang as SupportedLanguage)) {
    return <LanguageRedirect />;
  }

  return (
    <PageTransition key={location.pathname}>
      <Outlet />
    </PageTransition>
  );
};

export const LanguageRedirect = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if already on a language-prefixed route
    const pathParts = location.pathname.split('/').filter(Boolean);
    if (pathParts.length > 0 && supportedLanguages.includes(pathParts[0] as SupportedLanguage)) {
      return; // Already on a valid language route
    }

    // Get browser language or fallback to French
    const browserLang = navigator.language.split('-')[0];
    const detectedLang = supportedLanguages.includes(browserLang as SupportedLanguage) 
      ? browserLang 
      : 'fr';
    
    // Set i18n language
    if (i18n.language !== detectedLang) {
      i18n.changeLanguage(detectedLang);
    }

    // Build new path with language prefix - PRESERVE HASH FOR OAUTH CALLBACK
    const pathWithoutLang = location.pathname === '/' ? '' : location.pathname;
    const newPath = `/${detectedLang}${pathWithoutLang}${location.search}${location.hash}`;
    navigate(newPath, { replace: true });
  }, [i18n, navigate, location]);

  return null;
};

export default LanguageWrapper;
